import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import PageLoader from "../components/common/PageLoader";
import PersonCell from "../components/common/PersonCell";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import EmptyState from "../components/common/EmptyState";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";

function AddCustomerButton() {
  return (
    <Link to="/customers/new" className="btn btn-primary">
      <iconify-icon icon="solar:add-circle-outline"></iconify-icon>
      Add Customer
    </Link>
  );
}

export default function CustomersPage() {
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(searchParams.get("q") || "");

  async function loadCustomers(search = "") {
    const payload = await agentApi.get(`/travel-partner/customers?limit=25&page=1${search ? `&search=${encodeURIComponent(search)}` : ""}`);
    const data = payload?.data || payload || {};
    setCustomers(Array.isArray(data.customers) ? data.customers : Array.isArray(data.data) ? data.data : []);
  }

  useEffect(() => {
    const nextQuery = searchParams.get("q") || "";
    let alive = true;
    async function run() {
      try {
        setQuery(nextQuery);
        setLoading(true);
        await loadCustomers(nextQuery);
      } catch (fetchError) {
        if (alive) {
          showApiError(fetchError, "LOAD_CUSTOMERS");
        }
      } finally {
        if (alive) setLoading(false);
      }
    }
    run();
    return () => {
      alive = false;
    };
  }, [searchParams]);

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Customers"
          title="Agent-owned customers"
          items={[DASHBOARD_CRUMB, { label: "Customers" }]}
          actions={
            <div className="page-toolbar">
              <input
                type="search"
                className="form-control page-search"
                placeholder="Search customers"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={async (event) => {
                  if (event.key === "Enter") {
                    try {
                      setLoading(true);
                      await loadCustomers(query);
                    } catch (fetchError) {
                      showApiError(fetchError, "SEARCH_CUSTOMERS");
                    } finally {
                      setLoading(false);
                    }
                  }
                }}
              />
              <AddCustomerButton />
            </div>
          }
        />

        <div className="card border-0 shadow-sm">
          <div className="card-body p-24">
            {loading || customers.length > 0 ? <h5 className="mb-3">Customer list</h5> : null}
            {loading ? <PageLoader message="Loading customers..." /> : null}
            {!loading && customers.length === 0 ? (
              <EmptyState
                variant="customers"
                title={query.trim() ? "No customers match your search" : "No customers found for this agent."}
                description={query.trim()
                  ? "Try a different search term, or add a new customer."
                  : "Add a customer to assign them to upcoming bookings."}
                action={<AddCustomerButton />}
              />
            ) : null}
            {!loading && customers.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Status</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.cust_id}>
                      <td><PersonCell name={`${customer.cust_name || ""} ${customer.cust_last_name || ""}`.trim()} /></td>
                      <td className="text-break">{customer.cust_email}</td>
                      <td>{customer.cust_mobile}</td>
                      <td><StatusBadge value={customer.cust_status || "active"} /></td>
                      <td className="text-end">
                        <Link
                          to={`/customers/${customer.cust_id}/edit`}
                          className="agent-icon-btn"
                          title="Edit customer"
                          aria-label="Edit customer"
                        >
                          <iconify-icon icon="solar:pen-outline"></iconify-icon>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : null}
          </div>
        </div>
      </div>
    </AgentLayout>
  );
}
